<template>
    <div class="flex flex-col lg:h-full">
        <!-- <p class="mb-6 text-xl font-bold text-primary-700">Booking Summary</p> -->
        <div class="grid w-full grid-cols-12 gap-4 pb-4 text-darkgray-800">
            <div class="col-span-4">
                <p class="font-bold">Branch</p>
            </div>
            <div class="col-span-8 text-right">
                <p>{{ selectedBranch || '--' }}</p>
            </div>
            <div class="col-span-4">
                <p class="font-bold">Patient</p>
            </div>
            <div class="col-span-8 text-right capitalize">
                <p>{{ selectedPatient?.selected || '--' }}</p>
            </div>
            <div class="col-span-4">
                <p class="font-bold">Package</p>
                <p class="pl-5 font-bold">Add-ons</p>
            </div>
            <div class="col-span-8 text-right">
                <p>{{ selectedPackage || '--' }}</p>
                <template v-if="selectedAddOns">
                    <p class="text-xs" v-for="(selectedAddOn, index) in selectedAddOns" :key="index">{{ selectedAddOn.item_name }}</p>
                </template>
                <template v-else>
                    <p>--</p>
                </template>
            </div>
            <div class="col-span-4">
                <p class="font-bold">Date</p>
            </div>
            <div class="col-span-8 text-right">
                <p>{{ selectedDate || '--' }}</p>
            </div>
            <div class="col-span-4">
                <p class="font-bold">Time</p>
            </div>
            <div class="col-span-8 text-right">
                <p>{{ selectedTime || '--' }}</p>
            </div>
        </div>
        <div class="w-full mt-auto text-darkgray-800">
            <div v-if="route.path === '/booking/select-date-and-time'" class="pt-4 mb-4 border-t border-gray-40">
                <div class="relative">
                    <text-input
                        v-model="discountCode"
                        id="discount-code"
                        type="text"
                        placeholder="Discount Code"
                        custom-class="pr-20 uppercase"
                        :disabled="discount > 0"
                        :error="errors.code ? errors.code[0] : ''"
                    />
                    <p 
                        @click="applyDiscount()" 
                        class="absolute right-10 top-[25%] text-primary-500 text-sm font-bold cursor-pointer"
                        :class="[discount > 0? 'text-red-600' : '', loader ? 'opacity-50 cursor-not-allowed' : '']"
                    >
                        {{discount > 0 ? 'REMOVE' : 'APPLY'}}
                    </p>
                </div>
            </div>
            <div class="grid grid-cols-12 gap-4 pb-4 mb-4 border-b border-gray-40">
                <div class="col-span-4">
                    <p class="font-bold">Package Fee</p>
                </div>
                <div class="col-span-8 text-right">
                    <p>{{ formatter.format(packageFee) || 0.00 }}</p>
                </div>
                <div class="col-span-4">
                    <p class="font-bold">Misc. Fees</p>
                </div>
                <div class="col-span-8 text-right">
                    <p>{{ formatter.format(miscFee) || 0.00 }}</p>
                </div>
                <div class="col-span-4">
                    <p class="font-bold">Discount</p>
                </div>
                <div 
                    class="col-span-8 text-right "
                    :class=" discount > 0  ? 'text-red-500' : null "
                >
                    <p> - {{ discount > 0  ? formatter.format(discount) : '' }}</p>
                </div>
                <div class="col-span-full">
                    <!-- <p v-if="summaryNotes" class="text-sm text-neutral-400">NOTE: {{ summaryNotes?.summary_note_title }}</p> -->
                    <div v-if="summaryNotes" class="text-sm text-neutral-400" v-html="summaryNotes?.summary_note_title"></div>
                </div>
            </div>
            <div class="grid grid-cols-12 gap-2.5 mb-5"
            :class="totalColor ? totalColor : 'text-darkgray-800'">
                <div class="col-span-6">
                    <p class="text-xl font-bold">Total</p>
                </div>
                <div class="col-span-6 text-right">
                    <p class="text-xl font-bold">{{ formatter.format(totalFee) || 0.00 }}</p>
                </div>
            </div>
            <ButtonComponent
                :disabled="!isLastStep || isLoader"
                @click.prevent="$emit('book-now')"
                class="w-full"
                text="Request Booking"
            />
        </div>
    </div>
    <PageLoader v-if="loader" />
</template>
<script setup lang="ts">
import ButtonComponent from '@/components/ButtonComponent.vue';

import PageLoader from "@/components/PageLoader.vue";
import { useBookingStore } from '@/store/booking';
import { computed, onMounted, ref } from 'vue';
import { formatter } from "@/helpers/NumberHelper";
import TextInput from '@/components/inputs/TextInput.vue';
import { useRoute, useRouter } from 'vue-router';
import booking from '@/classes/Booking';
import Pages from '@/classes/Pages';

defineProps ({
    totalColor: {
        type: String,
    },
    defaultTitle: {
        type: Boolean,
        default: true
    },
    hideNotification: {
        type: Boolean,
        default: false
    },
    isloader: {
        type: Boolean,
        default: false
    }
});

const route = useRoute()
const router = useRouter();
// State Instance
const bookingStoreState = useBookingStore();

// Data
const loader = ref(false);
const selectedBranch = computed(() => bookingStoreState.bookingSummary?.selectedBranch);
const selectedPatient = computed(() => bookingStoreState.bookingSummary?.selectedPatient);
const selectedPackage = computed(() => bookingStoreState.bookingSummary?.selectedPackage);
const selectedAddOns = computed(() => bookingStoreState.bookingSummary?.selectedAddOns);
const selectedDate = computed(() => bookingStoreState.bookingSummary?.selectedDate);
const selectedTime = computed(() => bookingStoreState.bookingSummary?.selectedTime);
const packageFee = computed(() => bookingStoreState.bookingSummary?.packageFee);
const miscFee = computed(() => bookingStoreState.bookingSummary?.miscFee);
const totalFee = computed(() => bookingStoreState.bookingSummary?.totalFee);

const discountCode = ref<string|null>(bookingStoreState.bookingPayload?.discount_code)
const discount = computed(() => bookingStoreState.bookingSummary?.discount);
const errors = ref([])
const summaryNotes = ref('')

//METHODS
const applyDiscount = async () => {
    loader.value = true;
    
    if(discount.value < 1){

        discountCode.value = discountCode.value ? discountCode.value.toUpperCase() : null;

        await booking.applyVoucher({
            ...bookingStoreState.bookingPayload,
            rider_ids: bookingStoreState.bookingPayload.riders,
            code: discountCode.value
        })
        .then((result: any) => {
            bookingStoreState.bookingSummary.discount = result.data.applied_discount_fee
            bookingStoreState.bookingSummary.totalFee = totalFee.value - result.data.applied_discount_fee
            bookingStoreState.bookingPayload.other_charges_fee = totalFee.value
            
            bookingStoreState.bookingPayload.discount_code = result.data.voucher_code
            bookingStoreState.bookingPayload.discount_amount = result.data.applied_discount_fee

            errors.value = [];
        })
        .catch(err => {
            console.log(err)
            errors.value = err.response.data.errors;
        })
        .finally(() => {
            loader.value = false;
        })
    }
    else{
        bookingStoreState.bookingSummary.totalFee = totalFee.value + discount.value
        bookingStoreState.bookingSummary.discount = 0
        bookingStoreState.bookingPayload.other_charges_fee = totalFee.value
        discountCode.value = ''

        bookingStoreState.bookingPayload.discount_code = null;
        bookingStoreState.bookingPayload.discount_amount = null;

        loader.value = false;
    }
}

const fetchNotes = async() => {
    await Pages.fetchSlug('booking')
        .then((result: any) => {
            summaryNotes.value = result.data?.page.content;     
        })
        .catch((err) => {
            console.log(err)
        })
}

const isLastStep = computed( () => route.name == "SelectDateAndTime" || route.name == "ConfirmBooking");

onMounted(async () => {
    await fetchNotes()
})
</script>